import CustomerStats from "./CustomerStats";
import SendIcon from "@material-ui/icons/Send";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Switch from "@material-ui/core/Switch";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ReceiptIcon from "@material-ui/icons/Receipt";
import NotificationsActiveIcon from "@material-ui/icons/NotificationsActive";
import SyncIcon from "@material-ui/icons/Sync";
import ReplayIcon from "@material-ui/icons/Replay";
import AddShoppingCartIcon from "@material-ui/icons/AddShoppingCart";
import Collapse from "@material-ui/core/Collapse";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ShuffleIcon from "@material-ui/icons/Shuffle";
import StarsIcon from "@material-ui/icons/Stars";
import AccountBalanceWalletIcon from "@material-ui/icons/AccountBalanceWallet";
import CardGiftcardIcon from "@material-ui/icons/CardGiftcard";
import AttachMoneyIcon from "@material-ui/icons/AttachMoney";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import PersonOutlinedIcon from "@material-ui/icons/PersonOutlined";
import StorefrontIcon from "@material-ui/icons/Storefront";
import DevicesIcon from "@material-ui/icons/Devices";
import { useDialogStyles } from "../../theme/redesign";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import ConfirmationModal from "../../components/ConfirmationModal";
import {
  accountOccupancy,
  accountPrizeEligible,
  accountSellable,
  cartCanDelete,
  cartHasRecentAdd,
  cartHasRecentRenew,
  cartHasRecentUpdate,
  daysLeft,
  formatDate,
  formatWallet,
  isExpiredService,
  looksLikePhone,
  markCartPulse,
  markSubPulse,
  pointsSystemOn,
  storedPhone,
  money,
  moneyBoth,
  moneyPair,
  periodSlotCost,
  periodSlotPricePair,
  renewPriceOf,
  accountIsFullSale,
  todayIso,
  suggestAccountAndProfile,
  visibleValue,
} from "./helpers";
import { AccountMark, AccountOption, ProductMark, ProfileEditDialog, fetchNegocioAccount } from "./AccountPeek";
import { ContactAvatar, DialogClose, DueBadge, NegocioPager, PhoneFields, PhoneWithFlag, TrafficLight, tNegocio, useMobile, usePager } from "./shared";
import { ProductPeek } from "./Products";
import { CustomerChatButton } from "./CustomerChatButton";
import { CustomerServiceDialog, PointsSendTrigger, SendChannelButton, SendPromptDialog, ServiceRowActions, isBinancePaymentMethod, markServicePaid } from "./CustomerServiceDialog";
import { CartItemFields, usableCodeFilters } from "./CartItemFields";
import { hasNoValidity, needsAccountStock, needsSlots, productFamilyIds, productOfferLabel, productTitle, sellableProfiles, slotLabel, usesDeliveryCode, usesExternalId, usesGeneratedCode, usesMemberEmail } from "./catalog";
import { useStyles } from "./styles";

const SALE_TYPES = ["profile", "full", "combo"];

const sameUrl = (a, b) => String(a || "").replace(/\/$/, "").trim() === String(b || "").replace(/\/$/, "").trim();

const pickSalePaymentMethodId = (customer, payments, { paid, payWithWallet, payWithPrize } = {}) => {
  if (payWithWallet || payWithPrize) return null;
  const ids = [customer?.cartPaymentMethodId, customer?.cartPaymentMethodId2]
    .map((id) => Number(id))
    .filter(Boolean);
  if (!paid && ids.length) {
    const binance = (payments || []).find(
      (p) => ids.includes(Number(p.id)) && isBinancePaymentMethod(p)
    );
    if (binance) return binance.id;
  }
  return customer?.cartPaymentMethodId || null;
};

const slugFromUrl = (url) => {
  const part = String(url || "").split("/codigos/")[1] || "";
  const value = decodeURIComponent(part.split(/[?#]/)[0] || "");
  return value && !/^[a-f0-9]{32,}$/i.test(value) ? value : "";
};

const emptyCartForm = (preset) => {
  const landing = preset?.codesLandingUrl || "";
  const portalUrl = sameUrl(preset?.cartCodeUrl, landing) ? "" : preset?.cartCodeUrl || "";
  const portalUser = preset?.cartCodeUser || "";
  const portalPassword = preset?.cartCodePassword || "";
  return {
    contactId: preset?.contactId || "",
    customer: preset || null,
    cartPaymentMethodId: preset?.cartPaymentMethodId || "",
    cartPaymentMethodId2: preset?.cartPaymentMethodId2 || "",
    cartSaleType: preset?.cartSaleType || "profile",
    cartCodesEnabled: !!preset?.cartCodesEnabled,
    cartExternalPortal: false,
    cartCodeUrl: portalUrl,
    cartCodeUser: portalUser,
    cartCodePassword: portalPassword,
    codesLandingUrl: landing,
    codesLinkName: slugFromUrl(landing),
  };
};

export function CartConfigDialog({ open, customers, payments, preset, onClose, onSaved, lockCustomer = false }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const [form, setForm] = useState(emptyCartForm(preset));
  const [saving, setSaving] = useState(false);
  const [ensuring, setEnsuring] = useState(false);

  const presetContactId = Number(preset?.contactId || preset?.id) || null;
  const presetInList = !!presetContactId && (customers || []).some((c) => Number(c.contactId) === presetContactId);
  const customersRef = useRef(customers);
  customersRef.current = customers;

  // Solo al abrir (o cuando el cliente aparece en la lista): las recargas por socket no deben pisar el borrador.
  useEffect(() => {
    if (!open) return;
    const fromList = (customersRef.current || []).find((c) => Number(c.contactId) === presetContactId);
    setForm(emptyCartForm(fromList || preset));
  }, [open, preset, presetContactId, presetInList]);

  const patch = (next) => setForm((f) => ({ ...f, ...next }));
  const contactId = form.contactId || form.customer?.contactId || form.customer?.id;

  const saveCodesName = async (mode) => {
    if (!contactId) return;
    setEnsuring(true);
    try {
      const { data } = await api.post(`/negocio/customers/${contactId}/codes-token`, {
        mode,
        name: mode === "custom" ? form.codesLinkName : undefined,
      });
      const url = data?.codesLandingUrl || "";
      patch({ codesLandingUrl: url, codesLinkName: slugFromUrl(url) });
      toast.success(tNegocio("toastSaved"));
    } catch (e) {
      toastError(e);
    } finally {
      setEnsuring(false);
    }
  };

  const ensureLanding = async (id) => {
    if (!id || form.codesLandingUrl) return form.codesLandingUrl;
    setEnsuring(true);
    try {
      const { data } = await api.post(`/negocio/customers/${id}/codes-token`, { mode: "name" });
      const url = data?.codesLandingUrl || "";
      patch({ codesLandingUrl: url, codesLinkName: slugFromUrl(url) });
      return url;
    } catch (e) {
      toastError(e);
      return "";
    } finally {
      setEnsuring(false);
    }
  };

  const activePayments = (payments || []).filter((p) => p.isActive !== false);

  const save = async () => {
    if (!contactId) {
      toast.error(tNegocio("pages.services.cart.pickCustomer"));
      return;
    }
    if (!form.cartSaleType) {
      toast.error(tNegocio("pages.services.cart.saleType"));
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/negocio/customers/${contactId}/cart`, {
        cartPaymentMethodId: form.cartPaymentMethodId || null,
        cartPaymentMethodId2: form.cartPaymentMethodId2 || null,
        cartSaleType: form.cartSaleType,
        cartCodesEnabled: form.cartCodesEnabled,
        cartExternalPortal: !!form.cartExternalPortal,
        cartCodeUrl: form.cartExternalPortal ? form.cartCodeUrl : "",
        cartCodeUser: form.cartExternalPortal ? form.cartCodeUser : "",
        cartCodePassword: form.cartExternalPortal ? form.cartCodePassword : "",
      });
      if (data?.codesLandingUrl) patch({ codesLandingUrl: data.codesLandingUrl, codesLinkName: slugFromUrl(data.codesLandingUrl) });
      toast.success(tNegocio("toastSaved"));
      onSaved(Number(contactId));
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
      <DialogClose onClose={onClose} />
      <DialogTitle>{tNegocio("pages.services.cart.config")}</DialogTitle>
      <DialogContent dividers>
        <Autocomplete
          options={customers || []}
          disabled={!!lockCustomer}
          getOptionLabel={(c) => `${c.name || ""} · ${c.code || ""} · ${c.number || ""}`}
          getOptionSelected={(o, v) => Number(o?.contactId || o?.id) === Number(v?.contactId || v?.id)}
          value={form.customer}
          onChange={(_, v) => {
            const id = v?.contactId || v?.id || "";
            patch({ customer: v, contactId: id });
            if (form.cartCodesEnabled && id) ensureLanding(id);
          }}
          renderOption={(c) => (
            <div className={classes.agendaCell}>
              <ContactAvatar name={c.name} number={c.number} profilePicUrl={c.profilePicUrl} size={32} />
              <div>
                <div style={{ fontWeight: 700 }}>{c.name}</div>
                <div className={classes.captionBlock}>
                  {c.code}
                  {c.number ? ` · ${c.number}` : ""}
                </div>
              </div>
            </div>
          )}
          renderInput={(params) => (
            <TextField {...params} margin="dense" variant="outlined" label={tNegocio("pages.services.cart.pickCustomer")} />
          )}
        />
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("pages.services.cart.payment")}
          value={form.cartPaymentMethodId || ""}
          onChange={(e) => {
            const next = e.target.value;
            patch({
              cartPaymentMethodId: next,
              cartPaymentMethodId2:
                String(form.cartPaymentMethodId2) === String(next) ? "" : form.cartPaymentMethodId2,
            });
          }}
        >
          <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
          {activePayments.map((p) => (
            <MenuItem key={p.id} value={p.id}>
              {p.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          margin="dense"
          variant="outlined"
          label={tNegocio("pages.services.cart.payment2")}
          value={form.cartPaymentMethodId2 || ""}
          onChange={(e) => patch({ cartPaymentMethodId2: e.target.value })}
          helperText={tNegocio("pages.services.cart.payment2Hint")}
        >
          <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
          {activePayments
            .filter((p) => String(p.id) !== String(form.cartPaymentMethodId || ""))
            .map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
        </TextField>
        <div className={classes.sectionTitle}>{tNegocio("pages.services.cart.saleType")}</div>
        <div className={classes.productStats}>
          {SALE_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              className={`${classes.typeCard} ${form.cartSaleType === type ? classes.typeCardOn : ""}`}
              onClick={() => patch({ cartSaleType: type })}
            >
              <strong>{tNegocio(`pages.services.cart.saleTypes.${type}`)}</strong>
              <div className={classes.captionBlock}>{tNegocio(`pages.services.cart.saleTypeHints.${type}`)}</div>
            </button>
          ))}
        </div>
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={!!form.cartCodesEnabled}
              onChange={async (e) => {
                const checked = e.target.checked;
                patch({ cartCodesEnabled: checked });
                if (checked && contactId) await ensureLanding(contactId);
              }}
            />
          }
          label={tNegocio("pages.services.cart.codes")}
        />
        {form.cartCodesEnabled ? (
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("pages.codes.landingUrl")}
            value={ensuring ? tNegocio("pages.codes.generating") : form.codesLandingUrl || ""}
            InputProps={{ readOnly: true }}
            helperText={tNegocio("pages.codes.landingHint")}
          />
        ) : null}
        {form.cartCodesEnabled ? (
          <>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("pages.codes.linkName")}
              value={form.codesLinkName || ""}
              onChange={(e) => patch({ codesLinkName: e.target.value })}
              helperText={tNegocio("pages.codes.linkNameHint")}
            />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
              <Button size="small" variant="contained" color="primary" disabled={ensuring || !contactId || !String(form.codesLinkName || "").trim()} onClick={() => saveCodesName("custom")}>
                {tNegocio("pages.codes.saveLinkName")}
              </Button>
              <Button size="small" variant="outlined" color="primary" disabled={ensuring || !contactId} onClick={() => saveCodesName("name")}>
                {tNegocio("pages.codes.randomLinkName")}
              </Button>
            </div>
          </>
        ) : null}
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={!!form.cartExternalPortal}
              onChange={(e) =>
                patch({
                  cartExternalPortal: e.target.checked,
                  cartCodeUrl: e.target.checked ? form.cartCodeUrl : "",
                  cartCodeUser: e.target.checked ? form.cartCodeUser : "",
                  cartCodePassword: e.target.checked ? form.cartCodePassword : "",
                })
              }
            />
          }
          label={tNegocio("pages.codes.externalPortal")}
        />
        <div className={classes.captionBlock} style={{ margin: "-4px 0 8px" }}>
          {tNegocio("pages.codes.externalPortalHint")}
        </div>
        {form.cartExternalPortal ? (
          <>
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("pages.services.cart.codeUrl")}
              value={form.cartCodeUrl}
              onChange={(e) => patch({ cartCodeUrl: e.target.value })}
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("pages.services.cart.codeUser")}
              value={form.cartCodeUser}
              onChange={(e) => patch({ cartCodeUser: e.target.value })}
            />
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              type="password"
              label={tNegocio("pages.services.cart.codePassword")}
              value={form.cartCodePassword}
              onChange={(e) => patch({ cartCodePassword: e.target.value })}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" disabled={saving || ensuring} onClick={save}>
          {tNegocio("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const lastCartProduct = new Map();

export function AddCartItemDialog({ open, customer, payments = [], cartHasItems = false, onClose, onSaved }) {
  const dlg = useDialogStyles();
  const classes = useStyles();
  const saleType = customer?.cartSaleType || "profile";
  // Descuento de distribuidor: precio sugerido de compras nuevas (local a la centena, divisa a 2 decimales).
  const discount = customer?.customerType === "reseller" ? Number(customer?.discountPercent || 0) : 0;
  const slotPrice = (row, saleDays) => {
    const pair = periodSlotPricePair(row, saleDays);
    if (!(discount > 0)) return pair;
    const f = 1 - discount / 100;
    return {
      local: Number(pair.local) > 0 ? Math.round((Number(pair.local) * f) / 100) * 100 : pair.local,
      fx: Number(pair.fx) > 0 ? Math.round(Number(pair.fx) * f * 100) / 100 : pair.fx,
    };
  };
  const [products, setProducts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [combos, setCombos] = useState([]);
  const [comboId, setComboId] = useState("");
  const [comboSlots, setComboSlots] = useState([]);
  const [showComboSlots, setShowComboSlots] = useState(false);
  const [productId, setProductId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [profileId, setProfileId] = useState("");
  const [startDate, setStartDate] = useState(todayIso());
  const [days, setDays] = useState(30);
  const [currency, setCurrency] = useState("local");
  const [price, setPrice] = useState("");
  const [priceFx, setPriceFx] = useState("");
  // Precio escrito a mano: cambiar días o cuenta no lo recalcula (solo cambiar de producto).
  const manualPriceRef = useRef(false);
  const [notes, setNotes] = useState("");
  const [codesEnabled, setCodesEnabled] = useState(false);
  const [codeSubjects, setCodeSubjects] = useState([]);
  const [paid, setPaid] = useState(false);
  const [payWithWallet, setPayWithWallet] = useState(false);
  const [payWithPrize, setPayWithPrize] = useState(false);
  const [memberEmail, setMemberEmail] = useState("");
  const [deliveryCode, setDeliveryCode] = useState("");
  const [externalId, setExternalId] = useState("");
  const [saving, setSaving] = useState(false);
  const [autoSuggested, setAutoSuggested] = useState(false);
  const [pendingPick, setPendingPick] = useState(null);
  const [catalogReady, setCatalogReady] = useState(false);
  useEffect(() => {
    if (!open) return;
    setCatalogReady(false);
    setPendingPick(lastCartProduct.get(Number(customer?.contactId)) || null);
    setComboId("");
    setComboSlots([]);
    setShowComboSlots(false);
    setProductId("");
    setAccountId("");
    setProfileId("");
    setStartDate(todayIso());
    setDays(30);
    setCurrency("local");
    setPrice("");
    setPriceFx("");
    manualPriceRef.current = false;
    setNotes("");
    setCodesEnabled(false);
    setCodeSubjects([]);
    setPaid(false);
    setPayWithWallet(false);
    setPayWithPrize(false);
    setMemberEmail("");
    setDeliveryCode("");
    setExternalId("");
    setAutoSuggested(false);
    (async () => {
      try {
        const [p, a, c] = await Promise.all([
          api.get("/negocio/products"),
          api.get("/negocio/accounts"),
          saleType === "combo" ? api.get("/negocio/combos") : Promise.resolve({ data: { records: [] } }),
        ]);
        setAccounts(Array.isArray(a.data?.records) ? a.data.records : []);
        setCombos(Array.isArray(c.data?.records) ? c.data.records : []);
        setProducts(Array.isArray(p.data?.records) ? p.data.records : []);
        setCatalogReady(true);
      } catch (e) {
        toastError(e);
      }
    })();
  }, [open, saleType, customer?.cartCodesEnabled]);

  const typedAccounts = useMemo(() => {
    const eligible = payWithPrize ? accountPrizeEligible : accountSellable;
    return (accounts || []).filter((a) => eligible(a));
  }, [accounts, payWithPrize]);

  const visibleCombos = useMemo(() => {
    return (combos || []).filter((c) =>
      (c.items || []).every((item) => {
        const pid = Number(item.productId || item.product?.id);
        const type = c.saleType === "full" ? "full" : "profile";
        return (accounts || []).some((a) => Number(a.productId) === pid && accountSellable(a, type));
      })
    );
  }, [combos, accounts]);

  const visibleProducts = useMemo(() => {
    const stockIds = new Set(typedAccounts.map((a) => Number(a.productId)));
    const familyStock = new Set();
    (products || []).forEach((p) => {
      const family = productFamilyIds(products, p);
      if (family.some((id) => stockIds.has(id))) family.forEach((id) => familyStock.add(id));
    });
    return (products || []).filter((p) => {
      if (p.isActive === false) return false;
      if (!needsAccountStock(p)) return true;
      return familyStock.has(Number(p.id)) || stockIds.has(Number(p.id));
    });
  }, [products, typedAccounts]);

  const productAccounts = useMemo(() => {
    const prod = (products || []).find((p) => Number(p.id) === Number(productId));
    const family = new Set(productFamilyIds(products, prod));
    return typedAccounts.filter((a) => {
      if (!productId) return true;
      if (family.size) return family.has(Number(a.productId));
      return Number(a.productId) === Number(productId);
    });
  }, [typedAccounts, productId, products]);
  const account = productAccounts.find((a) => Number(a.id) === Number(accountId));
  const sellingFull = saleType === "full" || accountIsFullSale(account);
  const profiles = sellableProfiles(account);

  const selectedProduct = visibleProducts.find((p) => Number(p.id) === Number(productId));
  const selectedCombo = combos.find((c) => Number(c.id) === Number(comboId));
  const durationSiblings = useMemo(() => {
    if (!selectedProduct) return [];
    const family = new Set(productFamilyIds(products, selectedProduct));
    return visibleProducts
      .filter((p) => family.has(Number(p.id)))
      .sort((a, b) => Number(a.durationDays || 0) - Number(b.durationDays || 0));
  }, [visibleProducts, selectedProduct, products]);
  const itemCost = comboId
    ? (selectedCombo?.items || []).reduce((n, item) => n + Number(item.cost || 0), 0)
    : periodSlotCost(account || selectedProduct, hasNoValidity(selectedProduct) ? 30 : days);

  const extrasPatch = (next) => {
    if (next.startDate != null) setStartDate(next.startDate);
    if (next.days != null) {
      setDays(next.days);
      if (account && !manualPriceRef.current) {
        const saleDays = hasNoValidity(selectedProduct) ? 30 : next.days;
        const pair = slotPrice(account, saleDays);
        setPrice(pair.local);
        setPriceFx(pair.fx);
      }
    }
    if (next.currency != null) setCurrency(next.currency);
    if (next.price != null) {
      setPrice(next.price);
      manualPriceRef.current = next.price !== "";
    }
    if (next.priceFx != null) {
      setPriceFx(next.priceFx);
      manualPriceRef.current = manualPriceRef.current || next.priceFx !== "";
    }
    if (next.notes != null) setNotes(next.notes);
    if (next.codesEnabled != null) setCodesEnabled(next.codesEnabled);
    if (next.codeSubjects != null) setCodeSubjects(next.codeSubjects);
    if (next.paid != null) setPaid(next.paid);
    if (next.payWithWallet != null) {
      setPayWithWallet(next.payWithWallet);
      if (next.payWithWallet) {
        setPaid(true);
        setPayWithPrize(false);
      }
    }
    if (next.payWithPrize != null) {
      setPayWithPrize(next.payWithPrize);
      if (next.payWithPrize) {
        setPaid(true);
        setPayWithWallet(false);
        setDays(30);
        setComboId("");
      }
    }
  };

  const applyMoney = (prod, row, saleDays) => {
    if (manualPriceRef.current) return;
    const d = saleDays != null ? saleDays : hasNoValidity(prod) ? 30 : days || 30;
    const pair = slotPrice(row, Math.max(1, Number(d || 30)));
    setPrice(pair.local);
    setPriceFx(pair.fx);
  };

  const pickAccount = (id, { keepSuggest } = {}) => {
    setAccountId(id);
    if (!keepSuggest) setAutoSuggested(false);
    const row = productAccounts.find((a) => Number(a.id) === Number(id)) || (accounts || []).find((a) => Number(a.id) === Number(id));
    const prod = selectedProduct || row?.product;
    if (row && !accountIsFullSale(row) && saleType !== "full" && needsSlots(prod || row)) {
      const free = sellableProfiles(row);
      const pick = free.length ? free[Math.floor(Math.random() * free.length)] : null;
      setProfileId(pick?.id || "");
    } else {
      setProfileId("");
    }
    if (row) applyMoney(prod, row);
  };

  const pickProduct = (id) => {
    setProductId(id);
    manualPriceRef.current = false;
    const prod = (products || []).find((p) => Number(p.id) === Number(id)) || visibleProducts.find((p) => Number(p.id) === Number(id));
    const nextDays = prod && hasNoValidity(prod) ? 0 : 30;
    if (prod) setDays(nextDays);
    const suggestion = suggestAccountAndProfile(accounts, {
      product: prod,
      products,
      preferredType: saleType === "full" ? "full" : "profile",
    });
    setAccountId(suggestion.accountId || "");
    setProfileId(suggestion.profileId || "");
    setAutoSuggested(!!suggestion.accountId);
    applyMoney(prod, suggestion.account, hasNoValidity(prod) ? 30 : nextDays);
    if (codesEnabled) {
      setCodeSubjects(usableCodeFilters(prod).map((filter) => String(filter.subject).trim()));
    } else {
      setCodeSubjects([]);
    }
  };

  useEffect(() => {
    if (!open || !pendingPick || !catalogReady) return;
    setPendingPick(null);
    if (visibleProducts.some((p) => Number(p.id) === Number(pendingPick))) pickProduct(pendingPick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pendingPick, catalogReady, visibleProducts]);

  const assignForProduct = (product, preferredType, pool = accounts) => {
    const full = (products || []).find((p) => Number(p.id) === Number(product?.id || product?.productId)) || product;
    const suggestion = suggestAccountAndProfile(pool, {
      product: full,
      products,
      preferredType,
    });
    if (!suggestion.account) return null;
    const match = suggestion.account;
    const saleDays = hasNoValidity(full) ? 30 : days || 30;
    const pair = slotPrice(match, saleDays);
    const item = {
      productId: full.id || full.productId,
      accountId: match.id,
      profileId: preferredType === "full" || accountIsFullSale(match) ? null : suggestion.profileId || null,
      days: hasNoValidity(full) ? 0 : days || 30,
      startDate,
      notes,
      codesEnabled: !!customer?.cartCodesEnabled && codesEnabled,
      codeSubjects: !!customer?.cartCodesEnabled && codesEnabled
        ? usableCodeFilters(full).map((filter) => String(filter.subject).trim())
        : [],
      price: pair.local,
      priceFx: pair.fx,
      priceCurrency: currency || "local",
      cost: periodSlotCost(match, saleDays),
      costFx: null,
      costCurrency: "local",
    };
    if (preferredType !== "full" && !accountIsFullSale(match) && !item.profileId) return null;
    return item;
  };

  const buildComboSlots = (combo, pool = typedAccounts) => {
    const type = combo?.saleType === "full" ? "full" : "profile";
    const used = new Set();
    return (combo?.items || []).map((item) => {
      const product =
        item.product ||
        (products || []).find((p) => Number(p.id) === Number(item.productId)) ||
        item;
      const available = (pool || []).filter((a) => !used.has(Number(a.id)));
      const suggestion = suggestAccountAndProfile(available, {
        product,
        products,
        preferredType: type,
      });
      if (suggestion.accountId) used.add(Number(suggestion.accountId));
      return {
        productId: product.id || item.productId,
        product,
        accountId: suggestion.accountId || "",
        profileId: suggestion.profileId || "",
      };
    });
  };

  const comboType = selectedCombo?.saleType === "full" ? "full" : "profile";

  const accountsForSlot = (slot, index) => {
    const prod = slot?.product;
    const family = new Set(productFamilyIds(products, prod));
    const taken = new Set(
      comboSlots
        .filter((_, i) => i !== index)
        .map((row) => Number(row.accountId))
        .filter(Boolean)
    );
    return typedAccounts.filter((a) => {
      if (taken.has(Number(a.id))) return false;
      if (family.size) return family.has(Number(a.productId));
      return Number(a.productId) === Number(slot.productId);
    }).filter((a) => accountSellable(a, comboType));
  };

  const patchComboSlot = (index, patch) => {
    setComboSlots((rows) =>
      rows.map((row, i) => {
        if (i !== index) return row;
        const next = { ...row, ...patch };
        if (patch.accountId != null && patch.profileId == null) {
          const acc = typedAccounts.find((a) => Number(a.id) === Number(patch.accountId));
          if (acc && !accountIsFullSale(acc) && comboType !== "full" && needsSlots(next.product || acc)) {
            const free = sellableProfiles(acc);
            const pick = free.length ? free[Math.floor(Math.random() * free.length)] : null;
            next.profileId = pick?.id || "";
          } else {
            next.profileId = "";
          }
        }
        return next;
      })
    );
  };

  const save = async () => {
    if (!customer?.contactId) return;
    setSaving(true);
    try {
      let items = [];
      if (comboId && !payWithPrize) {
        const combo = combos.find((c) => Number(c.id) === Number(comboId));
        if (!combo) throw new Error("combo");
        const type = combo.saleType === "full" ? "full" : "profile";
        items = (comboSlots.length ? comboSlots : buildComboSlots(combo)).map((slot) => {
          const full =
            slot.product ||
            (products || []).find((p) => Number(p.id) === Number(slot.productId)) ||
            slot;
          const match = typedAccounts.find((a) => Number(a.id) === Number(slot.accountId));
          if (!match) return null;
          const saleDays = hasNoValidity(full) ? 30 : days || 30;
          const pair = periodSlotPricePair(match, saleDays);
          const item = {
            productId: full.id || slot.productId,
            accountId: match.id,
            profileId: type === "full" || accountIsFullSale(match) ? null : slot.profileId || null,
            days: hasNoValidity(full) ? 0 : days || 30,
            startDate,
            notes,
            codesEnabled: !!customer?.cartCodesEnabled && codesEnabled,
            codeSubjects:
              !!customer?.cartCodesEnabled && codesEnabled
                ? usableCodeFilters(full).map((filter) => String(filter.subject).trim())
                : [],
            price: pair.local,
            priceFx: pair.fx,
            priceCurrency: currency || "local",
            cost: periodSlotCost(match, saleDays),
            costFx: null,
            costCurrency: "local",
          };
          if (type !== "full" && !accountIsFullSale(match) && !item.profileId) return null;
          return item;
        }).filter(Boolean);
        if (items.length !== (combo.items || []).length) {
          toast.error(tNegocio("pages.services.cart.comboUnavailable"));
          setSaving(false);
          return;
        }
        const comboPrice = price !== "" ? Number(price) : Number(combo.price || 0);
        if (comboPrice) {
          const pair = moneyPair(comboPrice, priceFx);
          const share = pair.local / items.length;
          const fxShare = pair.fx / items.length;
          items = items.map((item) => ({
            ...item,
            price: share,
            priceFx: fxShare,
            priceCurrency: currency,
          }));
        }
      } else {
        if (!productId) {
          toast.error(tNegocio("fields.product"));
          setSaving(false);
          return;
        }
        const product = products.find((p) => Number(p.id) === Number(productId)) || selectedProduct;
        if (needsAccountStock(product) && !accountId) {
          toast.error(tNegocio("pages.services.cart.pickAccount"));
          setSaving(false);
          return;
        }
        if (needsAccountStock(product) && needsSlots(product) && !sellingFull && saleType !== "full" && !profileId) {
          toast.error(tNegocio("pages.services.cart.pickProfile"));
          setSaving(false);
          return;
        }
        if (usesMemberEmail(product) && !String(memberEmail || "").trim()) {
          toast.error(tNegocio("fields.memberEmail"));
          setSaving(false);
          return;
        }
        if (usesGeneratedCode(product) && !String(deliveryCode || "").trim()) {
          toast.error(tNegocio("fields.deliveryCode"));
          setSaving(false);
          return;
        }
        if (usesDeliveryCode(product) && !usesGeneratedCode(product) && !String(deliveryCode || "").trim() && !Number(product.codeStock || 0) && !accountId) {
          toast.error(tNegocio("pages.products.codes.needStock"));
          setSaving(false);
          return;
        }
        if (usesExternalId(product) && !String(externalId || "").trim()) {
          toast.error(tNegocio("fields.externalId"));
          setSaving(false);
          return;
        }
        items = [
          {
            productId,
            accountId: accountId || null,
            profileId: sellingFull || saleType === "full" || !needsSlots(product) ? null : profileId || null,
            days,
            startDate,
            notes,
            codesEnabled: !!customer?.cartCodesEnabled && codesEnabled,
            codeSubjects: !!customer?.cartCodesEnabled && codesEnabled ? codeSubjects : [],
            price: Number(price || 0),
            priceFx: priceFx !== "" && priceFx != null ? Number(priceFx) : moneyPair(price, null).fx,
            priceCurrency: currency,
            cost: itemCost,
            costFx: null,
            costCurrency: "local",
            memberEmail: memberEmail || null,
            deliveryCode: deliveryCode || null,
            externalId: externalId || null,
          },
        ];
      }
      const { data } = await api.post("/negocio/sales", {
        contactId: customer.contactId,
        paymentMethodId: pickSalePaymentMethodId(customer, payments, {
          paid,
          payWithWallet,
          payWithPrize,
        }),
        status: paid || payWithWallet || payWithPrize ? "paid" : "pending_payment",
        payWithWallet: !!payWithWallet,
        payWithPrize: !!payWithPrize,
        items,
      });
      markCartPulse(customer.contactId, "add");
      if (!comboId && productId) lastCartProduct.set(Number(customer.contactId), productId);
      toast.success(tNegocio("toastSaved"));
      const subscriptionIds = (data?.subscriptions || []).map((s) => Number(s.id)).filter(Boolean);
      onSaved({ subscriptionIds });
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
      <DialogClose onClose={onClose} />
      <DialogTitle>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <AddShoppingCartIcon fontSize="small" />
          {tNegocio("pages.services.cart.addItem")}
        </span>
      </DialogTitle>
      <DialogContent dividers>
        {saleType === "combo" && !payWithPrize ? (
          <TextField
            select
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("pages.combos.title")}
            value={comboId}
            onChange={(e) => {
              const id = e.target.value;
              setComboId(id);
              setProductId("");
              setAccountId("");
              setProfileId("");
              const combo = combos.find((c) => Number(c.id) === Number(id));
              if (combo) {
                setDays(combo.items?.[0]?.product?.durationDays || combo.durationDays || 30);
                const pair = moneyPair(combo.price, null);
                setCurrency("local");
                setPrice(pair.local);
                setPriceFx(pair.fx);
                setComboSlots(buildComboSlots(combo));
                setShowComboSlots(false);
              } else {
                setComboSlots([]);
                setShowComboSlots(false);
              }
            }}
          >
            <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
            {visibleCombos.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                <span className={classes.optionRow}>
                  {(c.items || []).slice(0, 3).map((item) => (
                    <ProductMark key={item.id || item.productId} product={item.product} size={22} circle />
                  ))}
                  {c.name} · {moneyBoth(c.price)} · {(c.items || []).length} {tNegocio("cols.product")}
                </span>
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        {!comboId ? (
          <>
            <TextField
              select
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("fields.product")}
              value={productId}
              onChange={(e) => pickProduct(e.target.value)}
            >
              <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
              {visibleProducts.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  <span className={classes.optionRow}>
                    <ProductMark product={p} size={24} circle />
                    {productOfferLabel(p, tNegocio)}
                    {p.codeStock ? ` · ${p.codeStock}` : ""}
                  </span>
                </MenuItem>
              ))}
            </TextField>
            {durationSiblings.length > 1 ? (
              <div className={classes.productStats} style={{ margin: "4px 0 10px" }}>
                {durationSiblings.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    className={`${classes.typeCard} ${Number(p.id) === Number(productId) ? classes.typeCardOn : ""}`}
                    onClick={() => pickProduct(p.id)}
                  >
                    <strong>{p.durationDays}d</strong>
                    <div className={classes.captionBlock}>{productOfferLabel(p, tNegocio, { days: false })}</div>
                  </button>
                ))}
              </div>
            ) : null}
            {productId && needsAccountStock(selectedProduct) ? (
            <>
            <TextField
              select
              fullWidth
              margin="dense"
              variant="outlined"
              label={tNegocio("cols.account")}
              value={accountId}
              onChange={(e) => pickAccount(e.target.value)}
            >
              <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
              {productAccounts.map((a) => (
                <MenuItem key={a.id} value={a.id}>
                  <AccountOption account={a} email={a.email} />
                </MenuItem>
              ))}
            </TextField>
            {autoSuggested && accountId ? (
              <div className={classes.captionBlock} style={{ marginBottom: 8 }}>
                {tNegocio("pages.services.cart.suggestedAccount")}
              </div>
            ) : null}
            </>
            ) : null}
            {productId && needsAccountStock(selectedProduct) && needsSlots(selectedProduct) && !sellingFull ? (
              <TextField
                select
                fullWidth
                margin="dense"
                variant="outlined"
                label={slotLabel(selectedProduct, tNegocio)}
                value={profileId}
                onChange={(e) => setProfileId(e.target.value)}
              >
                <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
                {profiles.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                    {p.pin ? ` · PIN ${p.pin}` : ""}
                    {p.memberEmail ? ` · ${p.memberEmail}` : ""}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {usesMemberEmail(selectedProduct) ? (
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                label={tNegocio("fields.memberEmail")}
                value={memberEmail}
                onChange={(e) => setMemberEmail(e.target.value)}
              />
            ) : null}
            {usesDeliveryCode(selectedProduct) || usesGeneratedCode(selectedProduct) ? (
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                label={tNegocio("fields.deliveryCode")}
                value={deliveryCode}
                onChange={(e) => setDeliveryCode(e.target.value)}
                helperText={
                  usesGeneratedCode(selectedProduct)
                    ? tNegocio("pages.services.cart.panelStockHint").replace(
                        "{n}",
                        String(accountOccupancy(account || {}).free || account?.slotCount || 0)
                      )
                    : Number(selectedProduct?.codeStock || 0)
                    ? tNegocio("pages.services.cart.codePoolHint").replace("{n}", String(selectedProduct.codeStock))
                    : tNegocio("pages.products.codes.empty")
                }
              />
            ) : null}
            {usesExternalId(selectedProduct) ? (
              <TextField
                fullWidth
                margin="dense"
                variant="outlined"
                label={tNegocio("fields.externalId")}
                value={externalId}
                onChange={(e) => setExternalId(e.target.value)}
              />
            ) : null}
          </>
        ) : (
          <div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <Button
                size="small"
                onClick={() => setShowComboSlots((v) => !v)}
                startIcon={showComboSlots ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              >
                {tNegocio("pages.services.cart.reviewSlots")}
              </Button>
              <Tooltip title={tNegocio("pages.services.cart.shuffleSlots")}>
                <IconButton
                  size="small"
                  onClick={() => setComboSlots(buildComboSlots(selectedCombo))}
                >
                  <ShuffleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </div>
            <Collapse in={showComboSlots}>
              {comboSlots.map((slot, index) => {
                const acc = typedAccounts.find((a) => Number(a.id) === Number(slot.accountId));
                const showProfile = comboType !== "full" && !accountIsFullSale(acc) && needsSlots(slot.product || acc);
                const profiles = sellableProfiles(acc);
                return (
                  <div key={`${slot.productId}-${index}`} className={classes.comboSlot}>
                    <div className={classes.optionRow} style={{ marginBottom: 6 }}>
                      <ProductMark product={slot.product} size={22} circle />
                      {productOfferLabel(slot.product, tNegocio)}
                    </div>
                    <TextField
                      select
                      fullWidth
                      margin="dense"
                      variant="outlined"
                      label={tNegocio("cols.account")}
                      value={slot.accountId || ""}
                      onChange={(e) => patchComboSlot(index, { accountId: e.target.value })}
                    >
                      <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
                      {accountsForSlot(slot, index).map((a) => (
                        <MenuItem key={a.id} value={a.id}>
                          <AccountOption account={a} email={a.email} />
                        </MenuItem>
                      ))}
                    </TextField>
                    {showProfile ? (
                      <TextField
                        select
                        fullWidth
                        margin="dense"
                        variant="outlined"
                        label={slotLabel(slot.product || acc, tNegocio)}
                        value={slot.profileId || ""}
                        onChange={(e) => patchComboSlot(index, { profileId: e.target.value })}
                      >
                        <MenuItem value="">{tNegocio("fields.none")}</MenuItem>
                        {profiles.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.name}
                            {p.pin ? ` · PIN ${p.pin}` : ""}
                            {p.memberEmail ? ` · ${p.memberEmail}` : ""}
                          </MenuItem>
                        ))}
                      </TextField>
                    ) : null}
                  </div>
                );
              })}
            </Collapse>
          </div>
        )}
        <CartItemFields
          startDate={startDate}
          days={days}
          currency={currency}
          price={price}
          priceFx={priceFx}
          cost={itemCost}
          notes={notes}
          codesEnabled={!!customer?.cartCodesEnabled && codesEnabled}
          codeSubjects={codeSubjects}
          codeFilters={usableCodeFilters(selectedProduct)}
          paid={paid || payWithWallet || payWithPrize}
          payWithWallet={payWithWallet}
          payWithPrize={payWithPrize}
          customer={customer}
          cartHasItems={!!cartHasItems}
          showCodes={!!customer?.cartCodesEnabled}
          showSubjectPicker={!comboId}
          hideDuration={hasNoValidity(selectedProduct)}
          onChange={extrasPatch}
        />
      </DialogContent>
      <DialogActions>
        <Button color="primary" variant="contained" disabled={saving} onClick={save}>
          {tNegocio("save")}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

export function CartRowActions({ cart, customer, now, onChanged, onRemoved, onNotifyCart, onRenewed, collections }) {
  const classes = useStyles();
  const [confirm, setConfirm] = useState(false);
  const [paidConfirm, setPaidConfirm] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const items = cart?.items || [];
  const contactId = customer?.contactId;
  const unpaidItems = items.filter(
    (s) => s.status !== "cancelled" && (s.sale?.status === "pending_payment" || s.status === "pending" || s.pendingPay)
  );
  const canDelete = cartCanDelete(items);
  const recentAdd = !collections && cartHasRecentAdd(items, now, contactId);
  const recentRenew = cartHasRecentRenew(items, now, contactId);
  const recentUpdate = !collections && cartHasRecentUpdate(items, now, contactId) && !recentRenew;
  const needsReminder = items.some((s) => {
    if (s.status === "cancelled") return false;
    const left = daysLeft(s.endDate);
    return left != null && left <= 3;
  });
  const expiredItems = items.filter(isExpiredService);
  const dueItems = items.filter((s) => {
    if (s.status === "cancelled") return false;
    if (isExpiredService(s)) return true;
    const left = daysLeft(s.endDate);
    return left != null && left <= 3;
  });
  const canRenew = collections ? dueItems.length > 0 : expiredItems.length > 0;
  const canSend = !!(looksLikePhone(customer?.number) || customer?.telegram);

  const remove = async () => {
    try {
      await api.delete(`/negocio/customers/${customer.contactId}/cart`);
      toast.success(tNegocio("toastSaved"));
      setConfirm(false);
      (onRemoved || onChanged)();
    } catch (e) {
      toastError(e);
    }
  };

  const markCartPaid = async () => {
    try {
      const seenSales = new Set();
      for (const s of unpaidItems) {
        const saleId = s.sale?.id || s.saleId;
        if (saleId && seenSales.has(saleId) && s.status !== "pending") continue;
        if (saleId) seenSales.add(saleId);
        await markServicePaid(s);
      }
      toast.success(tNegocio("pages.customers.markedPaid"));
      setPaidConfirm(false);
      onChanged?.();
    } catch (e) {
      toastError(e);
    }
  };

  const renewExpired = async () => {
    if (!contactId || renewing) return;
    setRenewing(true);
    try {
      await api.post(`/negocio/customers/${contactId}/cart/renew`, collections ? { due: true } : {});
      markCartPulse(contactId, "renewed");
      (items || []).forEach((s) => {
        if (s?.id && (collections ? dueItems.some((d) => Number(d.id) === Number(s.id)) : isExpiredService(s))) {
          markSubPulse(s.id, "renewed");
        }
      });
      toast.success(tNegocio("pages.services.cart.renewExpiredOk"));
      onRenewed?.({ kind: "renewed", customer, cart: true });
      onChanged?.();
    } catch (e) {
      toastError(e);
    } finally {
      setRenewing(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()} className={classes.collectActions} style={{ display: "inline-flex", alignItems: "center" }}>
      {customer?.contactId ? <CustomerChatButton customer={customer} /> : null}
      {canDelete ? (
        <Tooltip title={tNegocio("pages.services.cart.remove")}>
          <IconButton size="small" onClick={() => setConfirm(true)} style={{ color: "#c62828" }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {unpaidItems.length ? (
        <Tooltip title={`${tNegocio("markPaid")} (${unpaidItems.length})`}>
          <IconButton size="small" onClick={() => setPaidConfirm(true)} style={{ color: "#2e7d32" }}>
            <CheckCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {canRenew ? (
        <Tooltip title={tNegocio("pages.services.cart.renewExpired")}>
          <IconButton size="small" disabled={renewing} onClick={renewExpired} style={{ color: "#6a1b9a" }}>
            <ReplayIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : null}
      {recentRenew && canSend ? (
        <SendChannelButton
          title={tNegocio("pages.services.cart.sendRenewed")}
          icon={<ReplayIcon fontSize="small" />}
          color="#2e7d32"
          customer={customer}
          onSend={(channel) => onNotifyCart({ kind: "renewed", channel, contactId: customer.contactId })}
        />
      ) : null}
      {items.length && canSend && !collections ? (
        <SendChannelButton
          title={tNegocio("pages.services.cart.sendOrder")}
          icon={<ReceiptIcon fontSize="small" />}
          color="#5e35b1"
          customer={customer}
          onSend={(channel) => onNotifyCart({ kind: "order", channel, contactId: customer.contactId })}
        />
      ) : null}
      {needsReminder && canSend ? (
        <SendChannelButton
          title={tNegocio("pages.services.cart.sendReminder")}
          icon={<NotificationsActiveIcon fontSize="small" />}
          color="#ed6c02"
          customer={customer}
          onSend={(channel) => onNotifyCart({ kind: "reminder", channel, contactId: customer.contactId })}
        />
      ) : null}
      {recentUpdate && canSend ? (
        <SendChannelButton
          title={tNegocio("pages.services.cart.sendUpdated")}
          icon={<SyncIcon fontSize="small" />}
          color="#0277bd"
          customer={customer}
          onSend={(channel) => onNotifyCart({ kind: "update", channel, contactId: customer.contactId })}
        />
      ) : null}
      {recentAdd && canSend ? (
        <SendChannelButton
          title={tNegocio("pages.services.cart.sendLast")}
          icon={<AddShoppingCartIcon fontSize="small" />}
          color="#2e7d32"
          customer={customer}
          onSend={(channel) => onNotifyCart({ kind: "last", channel, contactId: customer.contactId })}
        />
      ) : null}
      <ConfirmationModal
        title={tNegocio("pages.services.cart.remove")}
        open={confirm}
        onClose={() => setConfirm(false)}
        onConfirm={remove}
      >
        {tNegocio("confirmRemoveService")}
      </ConfirmationModal>
      <ConfirmationModal
        title={`${tNegocio("markPaid")} (${unpaidItems.length})`}
        open={paidConfirm}
        onClose={() => setPaidConfirm(false)}
        onConfirm={markCartPaid}
      >
        {tNegocio("pages.customers.markPaidBody")}
      </ConfirmationModal>
    </div>
  );
}

const customerKind = {
  active: "active",
  expiring: "expiring",
  expired: "expired",
  none: "none",
};

function CustomerPeek({ contactId, onClose, onChanged }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!contactId) {
      setDetail(null);
      setEditing(null);
      return undefined;
    }
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get(`/negocio/customers/${contactId}`);
        if (!cancelled) setDetail(data);
      } catch (e) {
        toastError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  const reseller = detail?.customerType === "reseller";
  const services = detail?.subscriptions || [];

  return (
    <Dialog open={!!contactId} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
      <DialogClose onClose={onClose} />
      <DialogTitle>
        <div className={classes.detailTop}>
          <div className={classes.agendaCell}>
            <ContactAvatar name={detail?.name} number={detail?.number} profilePicUrl={detail?.profilePicUrl} size={56} />
            <div>
              {detail?.name || tNegocio("pages.customers.detailTitle")}
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
                {detail?.code ? (
                  <span className={`${classes.customerCode} ${reseller ? classes.codeReseller : classes.codeFinal}`}>{detail.code}</span>
                ) : null}
                {detail ? (
                  <span className={`${classes.typeMark} ${reseller ? classes.typeReseller : classes.typeFinal}`}>
                    {reseller ? <StorefrontIcon /> : <PersonOutlinedIcon />}
                  </span>
                ) : null}
                {detail ? <TrafficLight kind={customerKind[detail.kind] || "none"} /> : null}
              </div>
            </div>
          </div>
        </div>
      </DialogTitle>
      <DialogContent dividers>
        {loading && !detail ? (
          tNegocio("loading")
        ) : detail ? (
          <>
            <div className={classes.captionBlock}>
              <PhoneWithFlag number={detail.number} countryCode={detail.countryCode} countryName={detail.countryName} />
              {detail.telegram ? <div>{detail.telegram}</div> : null}
              {detail.notes ? <div>{detail.notes}</div> : null}
            </div>
            <CustomerStats customer={detail} />
            <div className={classes.sectionTitle}>{tNegocio("pages.customers.services")}</div>
            {services.length ? (
              services.map((sub) => (
                <div key={sub.id} className={classes.accountSaleRow}>
                  <ProductMark product={sub.product} size={28} circle link={false} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700 }}>{productTitle(sub.product) || sub.product?.name || "—"}</div>
                    <div className={classes.captionBlock}>{formatDate(sub.endDate)}</div>
                  </div>
                  <DueBadge endDate={sub.endDate} status={sub.status} />
                </div>
              ))
            ) : (
              <div className={classes.captionBlock}>{tNegocio("pages.services.empty")}</div>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        {detail ? (
          <Button onClick={() => setEditing({
            contactId: detail.contactId,
            name: detail.name || "",
            number: detail.number || "",
            notes: detail.notes || "",
            telegram: detail.telegram || "",
            customerType: detail.customerType || "final",
            discountPercent: detail.discountPercent ?? "",
            pointsEnabled: detail.pointsEnabled !== false,
          })}
          >
            {tNegocio("edit")}
          </Button>
        ) : null}
      </DialogActions>
      <Dialog open={!!editing} onClose={() => !savingEdit && setEditing(null)} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
        <DialogClose onClose={() => !savingEdit && setEditing(null)} disabled={savingEdit} />
        <DialogTitle>{tNegocio("edit")}</DialogTitle>
        <DialogContent dividers>
          <div className={classes.typeChoiceLabel}>{tNegocio("cols.type")}</div>
          <div className={classes.typeChoiceRow}>
            <button
              type="button"
              className={`${classes.typeChoice} ${(editing?.customerType || "final") === "final" ? classes.typeChoiceOn : ""}`}
              onClick={() => setEditing((d) => ({ ...d, customerType: "final" }))}
            >
              <PersonOutlinedIcon />
              {tNegocio("pages.customers.types.final")}
            </button>
            <button
              type="button"
              className={`${classes.typeChoice} ${editing?.customerType === "reseller" ? classes.typeChoiceOn : ""}`}
              onClick={() => setEditing((d) => ({ ...d, customerType: "reseller" }))}
            >
              <StorefrontIcon />
              {tNegocio("pages.customers.types.reseller")}
            </button>
            {pointsSystemOn() ? (
              <button
                type="button"
                className={`${classes.typeChoice} ${editing?.pointsEnabled !== false ? classes.typeChoiceOn : ""}`}
                onClick={() => setEditing((d) => ({ ...d, pointsEnabled: d.pointsEnabled === false }))}
              >
                <StarsIcon className={editing?.pointsEnabled !== false ? classes.metricPoints : undefined} />
                {editing?.pointsEnabled !== false
                  ? tNegocio("pages.customers.pointsOn")
                  : tNegocio("pages.customers.pointsOff")}
              </button>
            ) : null}
          </div>
          {editing?.customerType === "reseller" ? (
            <TextField
              fullWidth
              margin="dense"
              variant="outlined"
              type="number"
              inputProps={{ min: 0, max: 90, step: 0.5 }}
              label={tNegocio("pages.customers.discountPercent")}
              helperText={tNegocio("pages.customers.discountHint")}
              value={editing?.discountPercent ?? ""}
              onChange={(e) => setEditing((d) => ({ ...d, discountPercent: e.target.value }))}
            />
          ) : null}
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.name")}
            value={editing?.name || ""}
            onChange={(e) => setEditing((d) => ({ ...d, name: e.target.value }))}
          />
          <PhoneFields value={editing?.number || ""} onChange={(number) => setEditing((d) => ({ ...d, number }))} />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            label={tNegocio("fields.telegram")}
            value={editing?.telegram || ""}
            onChange={(e) => setEditing((d) => ({ ...d, telegram: e.target.value }))}
          />
          <TextField
            fullWidth
            margin="dense"
            variant="outlined"
            multiline
            rows={2}
            label={tNegocio("fields.notes")}
            value={editing?.notes || ""}
            onChange={(e) => setEditing((d) => ({ ...d, notes: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            disabled={savingEdit}
            onClick={async () => {
              if (!editing) return;
              if (!String(editing.name || "").trim()) {
                toast.error(tNegocio("pages.customers.nameRequired") || tNegocio("fields.name"));
                return;
              }
              const hasPhone = looksLikePhone(editing.number);
              const hasTelegram = String(editing.telegram || "").replace(/^@+/, "").trim();
              if (!hasPhone && !hasTelegram) {
                toast.error(tNegocio("pages.customers.phoneOrTelegram"));
                return;
              }
              setSavingEdit(true);
              try {
                await api.post("/negocio/customers", { ...editing, number: storedPhone(editing.number) });
                const { data } = await api.get(`/negocio/customers/${contactId}`);
                setDetail(data);
                setEditing(null);
                toast.success(tNegocio("toastSaved"));
                onChanged?.();
              } catch (e) {
                toastError(e);
              } finally {
                setSavingEdit(false);
              }
            }}
          >
            {tNegocio("save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export function PurchasedItemDialog({ open, item, customer, payments, onClose, onPeekAccount, onChanged, onNotify, onRenewed }) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const [serviceDialog, setServiceDialog] = useState(null);
  const [profileEdit, setProfileEdit] = useState(null);
  const [profileAccount, setProfileAccount] = useState(null);
  const [localProfile, setLocalProfile] = useState(null);
  const [accountLive, setAccountLive] = useState(null);
  const [productOpen, setProductOpen] = useState(false);

  useEffect(() => {
    setLocalProfile(null);
    setProfileEdit(null);
    setProfileAccount(null);
    setAccountLive(null);
    setProductOpen(false);
    if (!open || !item?.accountId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const acc = await fetchNegocioAccount(item.accountId);
        if (!cancelled && acc) setAccountLive(acc);
      } catch (e) {
        /* keep nested account */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [item?.id, item?.accountId, open]);

  const profile = localProfile || item?.profile;
  const account = accountLive || item?.account;
  const openProfile = async () => {
    if (!profile?.id && !item?.profileId) return;
    try {
      const acc = item.accountId ? (await fetchNegocioAccount(item.accountId)) || account : account;
      const pid = profile?.id || item.profileId;
      const full = (acc?.profiles || []).find((p) => Number(p.id) === Number(pid)) || profile;
      setProfileAccount(acc || account || null);
      setProfileEdit(full);
    } catch (e) {
      toastError(e);
    }
  };

  if (!open || !item) return null;
  const hasProfile = !!(profile?.id || item.profileId);
  const password = visibleValue(account?.password);
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" classes={{ paper: dlg.paper }}>
        <DialogClose onClose={onClose} />
        <DialogTitle>
          <div className={classes.productHead} style={{ alignItems: "center" }}>
            <ProductMark product={item.product} circle />
            <div>
              <button
                type="button"
                onClick={() => item.product && setProductOpen(true)}
                style={{
                  background: "none",
                  border: 0,
                  padding: 0,
                  font: "inherit",
                  fontWeight: 800,
                  color: "inherit",
                  cursor: item.product ? "pointer" : "default",
                  textAlign: "left",
                }}
              >
                {item.product ? productTitle(item.product) : tNegocio("cols.product")}
              </button>
              <div style={{ marginTop: 4 }}>
                <DueBadge endDate={item.endDate} status={item.status} />
              </div>
            </div>
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <div className={classes.sectionTitle}>{tNegocio("cols.account")}</div>
          <div
            className={classes.clickableRow}
            style={{ display: "inline-flex", padding: "6px 0" }}
            onClick={() => item.accountId && onPeekAccount?.(item.accountId)}
            role="button"
            tabIndex={0}
          >
            <AccountMark account={account} product={item.product} email={account?.email} size={32} />
          </div>
          {password ? (
            <div className={classes.infoField}>
              <div className={classes.captionBlock}>{tNegocio("fields.password")}</div>
              <div className={classes.accountPassword}>
                <VpnKeyIcon style={{ fontSize: 14 }} />
                {password}
              </div>
            </div>
          ) : null}
          <div className={classes.infoField}>
            <div className={classes.captionBlock}>{slotLabel(item.product || item.account, tNegocio)}</div>
            <div
              className={hasProfile ? classes.clickableRow : undefined}
              style={{ display: "inline-flex", padding: "4px 0", fontWeight: hasProfile ? 700 : 400 }}
              onClick={hasProfile ? openProfile : undefined}
              role={hasProfile ? "button" : undefined}
              tabIndex={hasProfile ? 0 : undefined}
            >
              {profile?.name || "—"}{profile?.pin ? ` · PIN ${profile.pin}` : ""}
            </div>
          </div>
          <div className={classes.infoField}>
            <div className={classes.captionBlock}>{tNegocio("cols.endDate")}</div>
            <div>{formatDate(item.endDate)}</div>
          </div>
          <div className={classes.infoField}>
            <div className={classes.captionBlock}>{tNegocio("cols.price")}</div>
            <div>{moneyBoth(renewPriceOf(item) || item.price, item.priceFx)}</div>
          </div>
          {item.notes ? (
            <div className={classes.infoField}>
              <div className={classes.captionBlock}>{tNegocio("fields.notes")}</div>
              <div>{item.notes}</div>
            </div>
          ) : null}
        </DialogContent>
        <DialogActions style={{ flexWrap: "wrap", justifyContent: "flex-end" }}>
          <ServiceRowActions
            sub={item}
            customer={customer}
            payments={payments}
            onEdit={(row) => setServiceDialog({ mode: "edit", sub: row })}
            onRenew={(row) => setServiceDialog({ mode: "renew", sub: row })}
            onNotify={onNotify}
            onDeleted={() => {
              onClose();
              onChanged();
            }}
            onChanged={onChanged}
          />
        </DialogActions>
      </Dialog>
      <CustomerServiceDialog
        open={!!serviceDialog}
        mode={serviceDialog?.mode}
        sub={serviceDialog?.sub}
        customer={customer}
        onClose={() => setServiceDialog(null)}
        onSaved={(mode, row) => {
          setServiceDialog(null);
          onChanged();
          if (mode === "renew") {
            onRenewed?.({ kind: "renew", customer, subscriptionId: row?.id || serviceDialog?.sub?.id });
          }
        }}
      />
      <ProfileEditDialog
        profile={profileEdit}
        account={profileAccount}
        onClose={() => setProfileEdit(null)}
        onSaved={(updated) => {
          setLocalProfile(updated);
          setProfileEdit(null);
          onChanged?.();
        }}
      />
      <ProductPeek open={productOpen} product={item.product} onClose={() => setProductOpen(false)} />
    </>
  );
}

export function CartDetailDialog({
  open,
  cart,
  customer,
  payments,
  now,
  onClose,
  onEditConfig,
  onAddItem,
  onChanged,
  onNotify,
  onNotifyCart,
  onPeekAccount,
  onRenewed,
  collections,
}) {
  const classes = useStyles();
  const dlg = useDialogStyles();
  const isMobile = useMobile();
  const [serviceDialog, setServiceDialog] = useState(null);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [sendPrompt, setSendPrompt] = useState(null);
  const payment = (payments || []).find((p) => Number(p.id) === Number(customer?.cartPaymentMethodId));
  const payment2 = (payments || []).find((p) => Number(p.id) === Number(customer?.cartPaymentMethodId2));
  const items = cart?.items || [];
  // Se deriva de la lista actual para reflejar renovaciones; la copia solo cubre si sale del filtro.
  const item = picked ? items.find((s) => Number(s.id) === Number(picked.id)) || picked : null;
  const contactId = customer?.contactId || cart?.contactId;
  const pager = usePager(items, 10);

  useEffect(() => {
    if (!open) setCustomerOpen(false);
  }, [open]);

  if (!open) return null;

  const suggestRenew = (payload) => {
    if (onRenewed) onRenewed(payload);
    else setSendPrompt(payload);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile} scroll="paper" classes={{ paper: `${dlg.paper} ${classes.cartPaper}` }}>
        <DialogClose onClose={onClose} />
        <DialogTitle className={classes.cartDialogTitle} disableTypography>
          <div className={classes.cartTitleBar}>
            <Button size="small" onClick={onEditConfig}>{tNegocio("pages.services.cart.config")}</Button>
            {isMobile ? (
              <Tooltip title={tNegocio("pages.services.cart.addItem")}>
                <span>
                  <IconButton color="primary" onClick={onAddItem} disabled={!customer?.cartSaleType}>
                    <AddShoppingCartIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Button size="small" color="primary" variant="contained" startIcon={<AddShoppingCartIcon />} onClick={onAddItem} disabled={!customer?.cartSaleType}>
                {tNegocio("pages.services.cart.addItem")}
              </Button>
            )}
          </div>
          <div className={classes.detailTop}>
            <div className={`${classes.agendaCell} ${classes.cartDetailIdentity}`}>
              <ContactAvatar name={customer?.name || cart?.name} number={customer?.number || cart?.number} profilePicUrl={customer?.profilePicUrl} size={44} />
              <div>
                <span
                  style={{ fontWeight: 800, cursor: contactId ? "pointer" : "default" }}
                  onClick={() => contactId && setCustomerOpen(true)}
                  role={contactId ? "button" : undefined}
                  tabIndex={contactId ? 0 : undefined}
                >
                  {customer?.name || cart?.name}
                </span>
                <div className={classes.captionBlock}>
                  {[
                    customer?.code || cart?.code,
                    customer?.number || cart?.number,
                    customer?.cartSaleType
                      ? tNegocio(`pages.services.cart.saleTypes.${customer.cartSaleType}`)
                      : null,
                    payment?.name,
                    payment2?.name,
                  ].filter(Boolean).join(" · ")}
                </div>
                <div className={classes.cartDetailMetrics}>
                  {Number(customer?.walletBalance) > 0 ? (
                    <Tooltip title={tNegocio("pages.customers.wallet")}>
                      <div className={classes.metricCell} aria-label={`${tNegocio("pages.customers.wallet")}: ${formatWallet(customer.walletBalance, customer.walletCurrency)}`}>
                        <AccountBalanceWalletIcon fontSize="small" className={classes.metricPoints} />
                        <strong>{formatWallet(customer.walletBalance, customer.walletCurrency)}</strong>
                      </div>
                    </Tooltip>
                  ) : null}
                  {customer?.rafflePrize?.status === "pending" ? (
                    <Tooltip title={tNegocio("pages.services.cart.prizeReady")}>
                      <div className={classes.metricCell}>
                        <CardGiftcardIcon fontSize="small" className={classes.metricPoints} />
                      </div>
                    </Tooltip>
                  ) : Number(customer?.raffleTickets || 0) > 0 ? (
                    <Tooltip title={tNegocio("pages.services.cart.ticketsLabel")}>
                      <div className={classes.metricCell} aria-label={`${tNegocio("pages.services.cart.ticketsLabel")}: ${customer.raffleTickets}`}>
                        <CardGiftcardIcon fontSize="small" className={classes.metricPoints} />
                        <strong>{customer.raffleTickets}</strong>
                      </div>
                    </Tooltip>
                  ) : null}
                  <Tooltip title={tNegocio("pages.services.cart.cartTotal")}>
                    <div className={classes.metricCell} aria-label={`${tNegocio("pages.services.cart.cartTotal")}: ${money(cart?.amount)}`}>
                      <AttachMoneyIcon fontSize="small" className={classes.metricMoney} />
                      <strong>{money(cart?.amount)}</strong>
                    </div>
                  </Tooltip>
                </div>
                {customer && onNotifyCart ? (
                  <div style={{ marginTop: 2 }}>
                    <CartRowActions
                      cart={cart}
                      customer={customer}
                      now={now}
                      collections={collections}
                      onChanged={onChanged}
                      onRemoved={async () => {
                        await onChanged?.();
                        onClose();
                      }}
                      onNotifyCart={onNotifyCart}
                      onRenewed={suggestRenew}
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          {customer?.cartCodesEnabled ? (
            <div className={classes.cartCodeLine}>
              <span>{tNegocio("pages.codes.landingUrl")}: {customer.codesLandingUrl || customer.cartCodeUrl || "—"}</span>
            </div>
          ) : null}
          <div className={classes.sectionTitle} style={{ margin: "8px 0 0" }}>{tNegocio("pages.customers.services")}</div>
        </DialogTitle>
        <DialogContent dividers className={classes.cartRecords}>
          {isMobile ? (
            <div className={classes.cartMobileList}>
              {pager.slice.map((s) => (
                <div
                  key={s.id}
                  className={`${classes.cartMobileItem} ${classes.clickableRow}`}
                  onClick={() => setPicked(s)}
                  role="button"
                  tabIndex={0}
                >
                  <div className={classes.cartMobileTop}>
                    <ProductMark product={s.product} size={32} circle />
                    <div className={classes.cartMobileBody}>
                      <div style={{ fontWeight: 800 }}>
                        {productOfferLabel(s.product, tNegocio, { days: false }) || s.product?.name}
                      </div>
                      <div className={classes.captionBlock}>
                        <AccountMark account={s.account} product={s.product} email={s.account?.email} />
                      </div>
                      <div className={classes.captionBlock} style={{ marginTop: 4 }}>
                        {formatDate(s.endDate)} · <DueBadge endDate={s.endDate} status={s.status} />
                      </div>
                    </div>
                  </div>
                  <div className={classes.cartMobileActions} onClick={(e) => e.stopPropagation()}>
                    <ServiceRowActions
                      sub={s}
                      customer={customer}
                      payments={payments}
                      onEdit={(row) => setServiceDialog({ mode: "edit", sub: row })}
                      onRenew={(row) => setServiceDialog({ mode: "renew", sub: row })}
                      onNotify={onNotify}
                      onDeleted={onChanged}
                      onChanged={onChanged}
                    />
                  </div>
                </div>
              ))}
              {!items.length ? (
                <div className={classes.captionBlock}>{tNegocio("pages.services.cart.noItems")}</div>
              ) : null}
            </div>
          ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{tNegocio("cols.product")}</TableCell>
                <TableCell>{tNegocio("cols.account")}</TableCell>
                <TableCell>{tNegocio("cols.endDate")}</TableCell>
                <TableCell>{tNegocio("cols.status")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {pager.slice.map((s) => (
                <TableRow
                  key={s.id}
                  hover
                  className={classes.clickableRow}
                  onClick={() => setPicked(s)}
                >
                  <TableCell>
                    <span className={classes.optionRow}>
                      <ProductMark product={s.product} size={24} circle />
                      {productOfferLabel(s.product, tNegocio, { days: false }) || s.product?.name}
                    </span>
                  </TableCell>
                  <TableCell>
                    <AccountMark account={s.account} product={s.product} email={s.account?.email} />
                  </TableCell>
                  <TableCell>{formatDate(s.endDate)}</TableCell>
                  <TableCell>
                    <DueBadge endDate={s.endDate} status={s.status} />
                  </TableCell>
                  <TableCell align="right" style={{ position: "static", whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                    <ServiceRowActions
                      sub={s}
                      customer={customer}
                      payments={payments}
                      onEdit={(row) => setServiceDialog({ mode: "edit", sub: row })}
                      onRenew={(row) => setServiceDialog({ mode: "renew", sub: row })}
                      onNotify={onNotify}
                      onDeleted={onChanged}
                      onChanged={onChanged}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {!items.length ? (
                <TableRow>
                  <TableCell colSpan={5}>{tNegocio("pages.services.cart.noItems")}</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          )}
        </DialogContent>
        <DialogActions>
          <NegocioPager
            count={pager.count}
            page={pager.page}
            rowsPerPage={pager.rowsPerPage}
            onChangePage={pager.setPage}
            onChangeRowsPerPage={pager.setRowsPerPage}
          />
        </DialogActions>
      </Dialog>
      <CustomerPeek
        contactId={customerOpen ? contactId : null}
        onClose={() => setCustomerOpen(false)}
        onChanged={onChanged}
      />
      <PurchasedItemDialog
        open={!!item}
        item={item}
        customer={customer}
        payments={payments}
        onClose={() => setPicked(null)}
        onPeekAccount={onPeekAccount}
        onChanged={onChanged}
        onNotify={onNotify}
        onRenewed={suggestRenew}
      />
      <CustomerServiceDialog
        open={!!serviceDialog}
        mode={serviceDialog?.mode}
        sub={serviceDialog?.sub}
        customer={customer}
        onClose={() => setServiceDialog(null)}
        onSaved={(mode, row) => {
          setServiceDialog(null);
          onChanged();
          if (mode === "renew") {
            suggestRenew({ kind: "renew", customer, subscriptionId: row?.id || serviceDialog?.sub?.id });
          }
        }}
      />
      <SendPromptDialog
        prompt={sendPrompt}
        onClose={() => setSendPrompt(null)}
        onAddAnother={() => {
          setSendPrompt(null);
          onAddItem?.();
        }}
        onSend={async (prompt, channel) => {
          if (prompt.cart || ["order", "last", "reminder", "update", "renewed"].includes(prompt.kind)) {
            await onNotifyCart?.({
              kind: prompt.kind || "last",
              channel,
              contactId: prompt.customer?.contactId,
              sinceUnix: prompt.sinceUnix,
              subscriptionIds: prompt.subscriptionIds,
            });
          } else {
            await onNotify?.({
              kind: prompt.kind || "renew",
              channel,
              contactId: prompt.customer?.contactId,
              subscriptionId: prompt.subscriptionId,
            });
          }
          setSendPrompt(null);
        }}
      />
    </>
  );
}
